import * as d3 from 'd3';
/*
Example:
const svg = d3.select('svg');

const x = 100;
const y = 50;
const width = 120;
const archHeight = 40;
const rectHeight = 100;
const totalHeight = archHeight + rectHeight;

// Draw base shape
drawMixedShape(
  svg,
  x,
  y,
  width,
  rectHeight,
  archHeight,
  10,
  'white',
  'black',
  'black',
  1,
  '#EDF9FF',
  true
);

// Draw colonial grills inside the shape
drawColonialGrillsInMixedShape(
  svg,
  x,
  y,
  width,
  totalHeight,
  10,
  2, // horLines
  3, // verLines
  'white',
  2,
  archHeight
);

*/

export function drawColonialGrillsInMixedShape(
    svg,
    initX,
    initY,
    width,
    height,
    frameWidth = 10,
    horLines = 1,
    verLines = 1,
    lineColor = 'white',
    grillThickness = 2,
    archHeight = 50,
    outlineColor = 'black',
    outlineWidth = 1
  ) {
    const rX = width / 2;
    const rY = archHeight;
  
    const left = initX;
    const right = initX + width;
    const centerY = initY + rY;
    const bottom = initY + height;
  
    // Create or select defs to hold clip path
    let defs = svg.select('defs');
    if (defs.empty()) {
      defs = svg.append('defs');
    }
  
    defs.select('#archClip').remove();
    const clipPath = defs.append('clipPath').attr('id', 'archClip');
  
    const pathD = `
      M ${left},${bottom}
      L ${left},${centerY}
      A ${rX},${rY} 0 0 1 ${right},${centerY}
      L ${right},${bottom}
      Z
    `;
    clipPath.append('path').attr('d', pathD);
  
    const grillsGroup = svg.append('g').attr('clip-path', 'url(#archClip)');
  
    const drawLine = (x1, y1, x2, y2) => {
      // Bottom layer: black outline
      grillsGroup.append('line')
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', x2)
        .attr('y2', y2)
        .attr('stroke', outlineColor)
        .attr('stroke-width', grillThickness + outlineWidth * 2)
        .attr('stroke-linecap', 'butt');
  
      // Top layer: white (or provided color) fill
      grillsGroup.append('line')
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', x2)
        .attr('y2', y2)
        .attr('stroke', lineColor)
        .attr('stroke-width', grillThickness)
        .attr('stroke-linecap', 'butt');
    };
  
    // Vertical lines
    if (verLines > 0) {
      const spacingX = width / (verLines + 1);
      for (let i = 1; i <= verLines; i++) {
        const xPos = left + spacingX * i;
        drawLine(xPos, initY, xPos, bottom);
      }
    }
  
    // Horizontal lines
    if (horLines > 0) {
      const spacingY = height / (horLines + 1);
      for (let i = 1; i <= horLines; i++) {
        const yPos = initY + spacingY * i;
        drawLine(left, yPos, right, yPos);
      }
    }
}
/* works good when hrect might be greater or equal than zero, e.g. >= 0 */
export function drawRadialGrillsInMixedShape(
    svg,
    initX,
    initY,
    width,
    height,
    frameWidth = 10,
    numRays = 3,
    lineColor = 'white',
    grillThickness = 2,
    archHeight = 50,
    outlineColor = 'black',
    outlineWidth = 1
  ) {
    const rX = width / 2;
    const rY = archHeight;
  
    const left = initX;
    const right = initX + width;
    const centerX = initX + rX;
    const centerY = initY + archHeight;
    const bottom = initY + height;
  
    // Clip path for arch + rectangle
    let defs = svg.select('defs');
    if (defs.empty()) {
      defs = svg.append('defs');
    }
  
    defs.select('#archClip').remove();
    const clipPath = defs.append('clipPath').attr('id', 'archClip');
  
    const pathD = `
      M ${left},${bottom}
      L ${left},${centerY}
      A ${rX},${rY} 0 0 1 ${right},${centerY}
      L ${right},${bottom}
      Z
    `;
    clipPath.append('path').attr('d', pathD);
  
    const grillsGroup = svg.append('g').attr('clip-path', 'url(#archClip)');
  
    const drawLine = (x1, y1, x2, y2) => {
      // Outline (under)
      grillsGroup.append('line')
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', x2)
        .attr('y2', y2)
        .attr('stroke', outlineColor)
        .attr('stroke-width', grillThickness + outlineWidth * 2)
        .attr('stroke-linecap', 'butt');
  
      // Main line (top)
      grillsGroup.append('line')
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', x2)
        .attr('y2', y2)
        .attr('stroke', lineColor)
        .attr('stroke-width', grillThickness)
        .attr('stroke-linecap', 'butt');
    };
  
    // === RAYS in the arch ===
    if (numRays > 0) {
      const startAngle = Math.PI; // left
      const endAngle = 0;         // right
      const angleStep = (startAngle - endAngle) / (numRays + 1);
    
      const startX = centerX;
      const startY = bottom;  // rays start at bottom line
    
      for (let i = 1; i <= numRays; i++) {
        const angle = startAngle - angleStep * i;
    
        // Endpoint on arch ellipse:
        const endX = centerX + rX * Math.cos(angle);
        const endY = centerY - rY * Math.sin(angle);
    
        // Draw line from bottom to arch endpoint directly:
        drawLine(startX, startY, endX, endY);
      }
    }        
}
/* works fine when hrect = 0 */  
export function drawRadialGrillsInPureSemiOval(
    svg,
    initX,
    initY,
    width,
    height,
    frameWidth = 10,
    numRays = 3,
    lineColor = 'white',
    grillThickness = 2,
    archHeight = 50,
    outlineColor = 'black',
    outlineWidth = 1
  ) {
    const rX = width / 2;
    const rY = archHeight;
  
    const left = initX;
    const right = initX + width;
    const centerX = initX + rX;
    const centerY = initY + archHeight;
    const bottom = initY + height;
  
    // Clip path for arch + rectangle
    let defs = svg.select('defs');
    if (defs.empty()) {
      defs = svg.append('defs');
    }
  
    defs.select('#archClip').remove();
    const clipPath = defs.append('clipPath').attr('id', 'archClip');
  
    const pathD = `
      M ${left},${bottom}
      L ${left},${centerY}
      A ${rX},${rY} 0 0 1 ${right},${centerY}
      L ${right},${bottom}
      Z
    `;
    clipPath.append('path').attr('d', pathD);
  
    const grillsGroup = svg.append('g').attr('clip-path', 'url(#archClip)');
  
    const drawLine = (x1, y1, x2, y2) => {
      // Outline (under)
      grillsGroup.append('line')
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', x2)
        .attr('y2', y2)
        .attr('stroke', outlineColor)
        .attr('stroke-width', grillThickness + outlineWidth * 2)
        .attr('stroke-linecap', 'butt');
  
      // Main line (top)
      grillsGroup.append('line')
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', x2)
        .attr('y2', y2)
        .attr('stroke', lineColor)
        .attr('stroke-width', grillThickness)
        .attr('stroke-linecap', 'butt');
    };
  
    // === RAYS in the arch ===
    if (numRays > 0) {
      const startAngle = Math.PI;     // left
      const endAngle = 0;             // right
      const angleStep = (startAngle - endAngle) / (numRays + 1);
  
      for (let i = 1; i <= numRays; i++) {
        const angle = startAngle - angleStep * i;
        const x = centerX + rX * Math.cos(angle);
        const y = centerY - rY * Math.sin(angle);  // <== FIXED here
        drawLine(centerX, centerY, x, y);
      }
    }
}
  
  


  
  